import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { makePrioStyles } from '../../../theme/utils';
import {
  Timeline,
  TimelineRef,
} from '@prio365/prio365-react-library/lib/Timeline/components/Timeline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment, { Moment } from 'moment';
import { add as addDate } from 'date-fns';
import {
  InteractJSEvent,
  TimelineGroup,
  TimelineItem,
} from '@prio365/prio365-react-library/lib/Timeline/types';
import {
  DateTimeString,
  MonthlyCloseId,
  ProjectId,
  TimeKeepingDayId,
  TimeKeepingDayType,
  TimeRecordId,
} from '../../../models/Types';
import { useDispatch, useSelector } from 'react-redux';
import {
  RootReducerState,
  getActiveProject,
  getMonthlyCloseMeTimeKeepingDayIds,
  getProjectByIdState,
  getSettingsByKey,
  getTimeKeepingDayByIdState,
  getTimeRecordIds,
  getTimeRecordsByIdState,
  getUserMe,
} from '../../../apps/main/rootReducer';
import { createSelector } from 'reselect';
import { ProjectByIdState } from '../../projects/reducers/projects';
import GroupHeader from './Timeline/GroupHeader';
import GroupSuffix from './Timeline/GroupSuffix';
import Flex from '../../../components/Flex';
import { PrioTheme } from '../../../theme/types';
import { useTheme } from 'react-jss';
import {
  debounceFunction,
  distinct,
  distinctByProperty,
  isTemporaryId,
} from '../../../util';
import {
  TimeAndLeaveManagementTimelineItem,
  timeTrackingLoadingSelector,
  timelineItemsSelector,
} from '../../timeAndLeaveManagement/selectors';
import {
  createTimeKeepingDay,
  debounceMonthlyCloseMe,
  deleteTimeKeepingDay,
  updateTimeKeepingDay,
} from '../actions';
import {
  CreateTimeKeepingEntry,
  TimeKeepingDay,
  TimeKeepingGeneral,
  TimeKeepingPrecision,
} from '../../../models/TimeKeeping';
import TimeKeepingTimelinieItem, {
  TimeEntry,
} from './Timeline/TimeKeepingTimelineItem';
import {
  debounceTimeRecordsMe,
  deleteMyTimeRecord,
  updateTimeRecord,
} from '../../timeRecords/actions';
import {
  apiCreateTimeKeepingDay,
  apiDeleteTimeKeepingDayMe,
  apiFetchTimeKeepingDaySuggestion,
  apiUpdateTimeKeeping,
} from '../api';
import { debounceAbsencesMe } from '../../absences/actions';
import { AbsenceProposal } from '../../../models/AbsenceProposal';
import TimeAndLeaveTimelineFormManager from '../../timeAndLeaveManagement/components/TimeAndLeaveTimelineFormManager';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  apiDeleteTimeRecords,
  apiUpdateTimeRecord,
} from '../../timeRecords/api';
import {
  TimeRecord,
  UpdateTimeRecordRequest,
} from '../../../models/TimeRecord';
import { mapAbsenceStateToColor } from '../../absences/utils';
import TimeTrackingSuggestionTimelineItem from './Timeline/TimeTrackingSuggestionTimelineItem';
import { apiFetchTimeTrackingSuggestions } from '../../calendar/api';
import { v4 as uuid } from 'uuid';
import TimeKeepingTimlinePopover from './Timeline/TimeKeepingTimlinePopover';
import { TimeKeepingByIdState } from '../reducers/timeKeepingDaysMe';
import { PrioSpinner } from '@prio365/prio365-react-library';
import { TimeRecordsByIdState } from '../../timeRecords/reducers/timeRecords';
import { User } from '../../../models/User';
import { Setting, SettingScope } from '../../../models/Settings';
import { useQuery } from '@tanstack/react-query';
import { apiFetchSettingsV2 } from '../../settings/api';
import { de, enUS, es, fr } from 'date-fns/locale';
import { toISOString } from '../utils';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  timeline5: {
    '& .prio-timeline-row-item-content .prio-timeline-row-item-grabber': {
      '&:first-child .prio-timeline-row-item-grabberIcon': {
        marginLeft: '4px !important',
      },
      '&:last-child .prio-timeline-row-item-grabberIcon': {
        marginLeft: '-4px !important',
      },
    },
  },
  timeline10: {
    '& .prio-timeline-row-item-content .prio-timeline-row-item-grabber': {
      '&:first-child .prio-timeline-row-item-grabberIcon': {
        marginLeft: '4px !important',
      },
      '&:last-child .prio-timeline-row-item-grabberIcon': {
        marginLeft: '2px !important',
      },
    },
  },
  groupHeaderTitle: {
    padding: theme.old.spacing.unit(0.5),
    width: '100%',
    '& .ant-select': {
      width: '100%',
    },
    '& .ant-select-single:not(.ant-select-customize-input) .ant-select-selector':
      {
        padding: 0,
        border: 'none',
      },
    '& .ant-select-single .ant-select-selector .ant-select-selection-search': {
      left: 0,
    },
  },
  addButton: {
    color: theme.old.palette.primaryColor,
  },
  formManager: {
    backgroundColor: theme.old.palette.backgroundPalette.main,
    maxHeight: '45%',
    boxShadow: theme.old.palette.boxShadow.regular,
    borderRadius: 2,
    margin: `${theme.old.spacing.unit(3)}px ${
      theme.old.spacing.baseSpacing
    }px ${theme.old.spacing.unit(0.5)}px ${theme.old.spacing.baseSpacing}px`,
  },
  spinnerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const createTemporaryId = () => {
  return `t-${uuid()}`;
};

const projectGroupsSelector = (
  projectIds: ProjectId[],
  items: TimeAndLeaveManagementTimelineItem[],
  day: DateTimeString
) =>
  createSelector<
    [(state: RootReducerState) => ProjectByIdState],
    TimelineGroup[]
  >(getProjectByIdState, (byId) =>
    distinctByProperty<TimelineGroup>(
      items.reduce((currentArray, { groupId, startDateTime, type }) => {
        if (
          !projectIds.includes(groupId) &&
          (type === 'timeRecord' || type === 'timeTrackingSuggestion') &&
          startDateTime.split('T')[0] === day
        ) {
          currentArray.push({
            id: groupId,
            title: byId[groupId]?.number ?? '',
          });
        }
        return currentArray;
      }, [] as TimelineGroup[]),
      'id'
    )
      .concat(
        projectIds.map((id) => {
          const project = byId[id];
          return {
            id,
            title: project.number,
          };
        })
      )
      .sort((a, b) => a.title.localeCompare(b.title))
  );

const timeKeepingListSelector = (date: string) =>
  createSelector<
    [
      (state: RootReducerState) => TimeKeepingByIdState,
      (state: RootReducerState) => TimeKeepingDayId[],
    ],
    TimeKeepingDay[] | null
  >(
    getTimeKeepingDayByIdState,
    (state) => getMonthlyCloseMeTimeKeepingDayIds(state, date),
    (byId, ids) => {
      const timeKeepingList = ids.map((id) => byId[id]);
      return timeKeepingList;
    }
  );

const calcItemOnMove: (
  item: TimeAndLeaveManagementTimelineItem,
  entriesStartTime: DateTimeString,
  sortedEntries: {
    startTime: string;
    endTime: string;
  }[]
) => TimeAndLeaveManagementTimelineItem = (
  item,
  entriesStartTime,
  sortedEntries
) => {
  const deltaInMinutes = moment(item.startDateTime).diff(
    moment(entriesStartTime),
    'minutes'
  );
  const newEntries = sortedEntries.map(({ startTime, endTime }) => ({
    startTime: moment(startTime)
      .add(deltaInMinutes, 'minutes')
      .toISOString(true)
      .split('.')[0],
    endTime: moment(endTime)
      .add(deltaInMinutes, 'minutes')
      .toISOString(true)
      .split('.')[0],
  }));
  return {
    ...item,
    entries: newEntries,
  };
};

const calcOnItemResize: (
  item: TimeAndLeaveManagementTimelineItem,
  entriesStartTime: DateTimeString,
  entriesEndTime: DateTimeString,
  sortedEntries: {
    startTime: string;
    endTime: string;
  }[]
) => TimeAndLeaveManagementTimelineItem = (
  item,
  entriesStartTime,
  entriesEndTime,
  sortedEntries
) => {
  const newEntries = sortedEntries.map(({ startTime, endTime }, index) => ({
    startTime: toISOString(index === 0 ? item.startDateTime : startTime),
    endTime: toISOString(
      index === sortedEntries.length - 1 ? item.endDateTime : endTime
    ),
  }));
  return {
    ...item,
    entries: newEntries,
  };
};

const debouncedApiFetchTimeTrackingSuggestions = debounceFunction(
  async (
    from: DateTimeString,
    to: DateTimeString,
    setTimeTrackingSuggestionItems: (
      value: TimeAndLeaveManagementTimelineItem[]
    ) => void,
    setTimeTrackingSuggestionLoading: (value: boolean) => void
  ) => {
    setTimeTrackingSuggestionLoading(true);
    const { data } = await apiFetchTimeTrackingSuggestions(from, to);
    if (data) {
      setTimeTrackingSuggestionItems(
        data.map((item) => {
          const startTime = moment
            .utc(item.start)
            .local()
            .toISOString(true)
            .split('.')[0];
          const endTime = moment
            .utc(item.end)
            .local()
            .toISOString(true)
            .split('.')[0];
          return {
            id: uuid(),
            groupId: item.projectId,
            title: item.name,
            startDateTime: startTime,
            endDateTime: endTime,
            type: 'timeTrackingSuggestion',
            value: item,
            entries: [
              {
                startTime: startTime,
                endTime: endTime,
              },
            ],
            state: 'ok',
            disabled: true,
          };
        })
      );
    }
    setTimeTrackingSuggestionLoading(false);
  },
  500
);

const snapToXMinutes = (
  precision: number,
  startTime: DateTimeString,
  endTime: string
) => {
  const _startTime = moment(
    Math.round(moment(startTime).valueOf() / (precision * 60 * 1000)) *
      (precision * 60 * 1000)
  )
    .toISOString(true)
    .split('.')[0];
  const _endTime = moment(
    Math.round(moment(endTime).valueOf() / (precision * 60 * 1000)) *
      (precision * 60 * 1000)
  )
    .toISOString(true)
    .split('.')[0];
  return {
    startTime: _startTime,
    endTime: _endTime,
  };
};

const buildVisibleEndDate = (day: DateTimeString, maxRange: DateTimeString) => {
  const endDate = new Date(`${day}T${maxRange}:00`);
  if (maxRange === '00:00') {
    return addDate(endDate, { days: 1 });
  }

  return endDate;
};

const timeRecordsSelector = (selectedDate: Moment) =>
  createSelector<
    [
      (state: RootReducerState) => TimeRecordsByIdState,
      (state: RootReducerState) => TimeRecordId[],
      (state: RootReducerState) => User,
    ],
    TimeRecord[]
  >(getTimeRecordsByIdState, getTimeRecordIds, getUserMe, (byId, ids, userMe) =>
    ids.reduce((currentArray, id) => {
      const timeRecord = byId[id];
      if (
        timeRecord &&
        timeRecord.contactId === userMe.id &&
        moment(timeRecord.day).isSame(selectedDate, 'day')
      ) {
        currentArray.push(timeRecord);
      }
      return currentArray;
    }, [] as TimeRecord[])
  );

const timeKeepingPrecisionToMinutes = (precision?: TimeKeepingPrecision) => {
  if (!precision) {
    return 15;
  }
  switch (precision) {
    case 'fifteenMinutes':
      return 15;
    case 'tenMinutes':
      return 10;
    default:
      return 5;
  }
};

const SETTING_SCOPE_SORT = {
  office: 0,
  global: 1,
  none: 2,
};

const fetchSetting = async (userMe: User): Promise<Setting | null> => {
  const officeId = userMe?.myOfficeId;
  const { result, data } = await apiFetchSettingsV2([
    { scopeId: '', eSettingScope: 'global', keys: ['TimeKeeping_General'] },
    ...(officeId
      ? [
          {
            scopeId: officeId,
            eSettingScope: 'office' as SettingScope,
            keys: ['TimeKeeping_General'],
          },
        ]
      : []),
  ]);
  if (result.ok && data) {
    return data.sort(
      (a, b) => SETTING_SCOPE_SORT[a.scope] - SETTING_SCOPE_SORT[b.scope]
    )[0];
  }
  return null;
};

const parseSettingValue = (
  setting: Setting | null
): TimeKeepingGeneral | null => {
  if (!setting) {
    return null;
  }
  const value = JSON.parse(setting.jsonValue) as TimeKeepingGeneral;
  return value;
};

const getDefaultCreateType = (setting: Setting | null): TimeKeepingDayType => {
  if (!setting) {
    return 'office';
  }
  const value = parseSettingValue(setting);
  switch (value?.editPolicy ?? value?.EditPolicy) {
    case 'editsAreAllowed': {
      return 'office';
    }
    default: {
      return 'homeOffice';
    }
  }
};

interface TimeKeepingDrawerContentProps {
  className?: string;
  initialDay?: DateTimeString;
  isVisible: boolean;
  monthlyCloseChange: (monthlyCloseId: MonthlyCloseId) => void;
}

export const TimeKeepingDrawerContent: React.FC<
  TimeKeepingDrawerContentProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { className, initialDay, isVisible, monthlyCloseChange } = props;
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const dateFnsLocale = { de, en: enUS, es, fr }[i18n.language] ?? de;
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const ref = useRef<TimelineRef>(null);

  const [day, setDay] = useState<DateTimeString>(
    (initialDay ?? moment().toISOString(true)).split('T')[0]
  );
  const [maxRange, setMaxRange] = useState<DateTimeString>('18:00');
  const [minRange, setMinRange] = useState<DateTimeString>('07:00');

  const currentMonth = useMemo(() => day.substring(0, 7), [day]);

  const timeKeepingPrecisionSetting = useSelector<RootReducerState, Setting[]>(
    (state) => getSettingsByKey(state, 'TimeKeeping_Precision')
  );
  const timeKeepingPrecision = useMemo(() => {
    const settingValue = JSON.parse(
      timeKeepingPrecisionSetting?.[0]?.jsonValue
    );

    return timeKeepingPrecisionToMinutes(
      settingValue?.precision ?? settingValue?.Precision
    );
  }, [timeKeepingPrecisionSetting]);

  const _items: TimeAndLeaveManagementTimelineItem[] = useSelector(
    timelineItemsSelector(currentMonth, minRange, maxRange)
  );

  const [timeTrackingSuggestionItems, setTimeTrackingSuggestionItems] =
    useState<TimeAndLeaveManagementTimelineItem[]>([]);

  const [timeTrackingSuggestionLoading, setTimeTrackingSuggestionLoading] =
    useState<boolean>(false);

  const isFetchingData = useSelector(timeTrackingLoadingSelector);

  const [temporaryItems, setTemporaryItems] = useState<
    TimeAndLeaveManagementTimelineItem[]
  >([]);
  const [timeKeepingSuggestion, setTimeKeepingSuggestion] =
    useState<TimeAndLeaveManagementTimelineItem>(null);

  const userMe = useSelector(getUserMe);

  const { data: setting } = useQuery({
    queryKey: ['settings', 'timeKeeping_general'],
    queryFn: async () => {
      return fetchSetting(userMe);
    },
    staleTime: 60 * 60 * 1000,
  });

  const timeKeepingGeneralSettings = useMemo(() => {
    return parseSettingValue(setting);
  }, [setting]);

  const items = useMemo(
    () => [
      ..._items,
      ...temporaryItems,
      ...timeTrackingSuggestionItems,
      ...(timeKeepingSuggestion ? [timeKeepingSuggestion] : []),
    ],
    [_items, temporaryItems, timeTrackingSuggestionItems, timeKeepingSuggestion]
  );

  const timelineKey = useMemo(() => {
    return items
      .map(
        (i) =>
          `${i.id}:${i.startDateTime}:${i.endDateTime}:${i.entries.map((e) => e.startTime + '-' + e.endTime).join('|')}`
      )
      .join(';');
  }, [items]);

  const activeProjectId = useSelector(getActiveProject);

  const [addedProjectIds, setAddedProjectIds] = useState<ProjectId[]>([]);

  const projectIds = useMemo(
    () =>
      distinct([
        ...addedProjectIds,
        ...(activeProjectId && activeProjectId !== 'me'
          ? [activeProjectId]
          : []),
      ]),
    [addedProjectIds, activeProjectId]
  );

  const projectGroups = useSelector(
    projectGroupsSelector(projectIds, items, day)
  );

  const groups: TimelineGroup[] = useMemo(
    () => [
      {
        id: 'timeKeeping',
        title: 'Arbeitszeit',
      },
      ...(projectGroups ?? []),
      {
        id: 'addProject',
        title: 'addProjekt',
        disabled: true,
      },
    ],
    [projectGroups]
  );

  const [selectedItem, setSelectedItem] =
    useState<TimeAndLeaveManagementTimelineItem>(null);

  // The still unsaved item lives twice: once in `temporaryItems` (rendered by
  // the timeline) and once in `selectedItem` (rendered by the form, and the
  // source of the entries sent to the API on save). Whenever a temporary item
  // is mutated the selection has to follow, otherwise changes made on the
  // timeline - e.g. adding a break - are dropped when the form is submitted.
  const syncSelectedItem = useCallback(
    (item: TimeAndLeaveManagementTimelineItem) => {
      setSelectedItem((currentSelectedItem) =>
        currentSelectedItem?.id === item.id ? item : currentSelectedItem
      );
    },
    []
  );

  const timeRecords = useSelector(timeRecordsSelector(moment(day)));

  const timeRecordSum = timeRecords.reduce(
    (currentSum, { timeRecordEntries }) => {
      return (
        currentSum +
        timeRecordEntries.reduce((_currentSum, { startTime, endTime }) => {
          return (
            _currentSum + moment(endTime).diff(moment(startTime), 'minutes')
          );
        }, 0)
      );
    },
    0
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleOnProjectSelect = useCallback(
    (projectId: ProjectId) => {
      setAddedProjectIds([...addedProjectIds, projectId]);
    },
    [addedProjectIds]
  );

  const handleOnProjectRemove = useCallback(
    (projectId: ProjectId) => {
      setAddedProjectIds(addedProjectIds.filter((id) => id !== projectId));
      setTemporaryItems(
        temporaryItems.filter((item) => item.groupId !== projectId)
      );
    },
    [addedProjectIds, temporaryItems]
  );

  const calcItemStyle = useCallback(
    (group: TimelineGroup, item: TimeAndLeaveManagementTimelineItem) => {
      const { type, state } = item;
      switch (type) {
        case 'absence': {
          return {
            backgroundColor: mapAbsenceStateToColor(
              (item.value as AbsenceProposal).absenceState,
              (item.value as AbsenceProposal).absenceType,
              theme
            ),
          };
        }
        case 'timeTrackingSuggestion': {
          return {
            backgroundColor: theme.old.palette.backgroundPalette.hover.main,
          };
        }
        case 'timeKeepingSuggestion': {
          return {
            backgroundColor: theme.old.palette.backgroundPalette.hover.main,
          };
        }
        case 'timeKeeping': {
          const { state: timeKeepingState } = item.value as TimeKeepingDay;
          let backgroundColor = undefined;
          if (timeKeepingState === 'approvalRequested') {
            backgroundColor = theme.old.palette.chromaticPalette.yellow;
          }
          if (timeKeepingState === 'notApproved') {
            backgroundColor = theme.old.palette.chromaticPalette.red;
          }
          if (state === 'temporary') {
            backgroundColor = theme.old.palette.backgroundPalette.hover.base;
          } else if (state === 'error') {
            backgroundColor = theme.old.palette.chromaticPalette.red;
          }
          return {
            backgroundColor,
          };
        }
        default: {
          let backgroundColor = undefined;
          if (state === 'temporary') {
            backgroundColor = theme.old.palette.backgroundPalette.hover.base;
          } else if (state === 'error') {
            backgroundColor = theme.old.palette.chromaticPalette.red;
          }
          return {
            backgroundColor,
          };
        }
      }
    },
    [theme]
  );

  const handleOnItemClick: (
    item: TimeAndLeaveManagementTimelineItem,
    group: TimelineGroup
  ) => void = useCallback((item, group) => {
    if (group.id === 'addProject') {
      setSelectedItem(null);
    } else {
      setSelectedItem(item);
    }
  }, []);

  const handleTimeKeepingUpdate: (
    item: TimeAndLeaveManagementTimelineItem,
    newItem: TimeAndLeaveManagementTimelineItem
  ) => Promise<TimeAndLeaveManagementTimelineItem> = useCallback(
    async (item, newItem) => {
      if (ref.current) {
        ref.current.replaceItem(
          newItem.groupId,
          { ...newItem, disabled: true },
          newItem.id
        );
      }
      let timeKeepingEntries: CreateTimeKeepingEntry[] = [];
      try {
        if (newItem.entries.length === 1) {
          const { data: suggestion } = await apiFetchTimeKeepingDaySuggestion(
            toISOString(newItem.entries[0].startTime),
            toISOString(newItem.entries[0].endTime)
          );
          if (suggestion) {
            timeKeepingEntries = suggestion.timeKeepingEntries.map(
              ({ startTime, endTime }) =>
                snapToXMinutes(timeKeepingPrecision, startTime, endTime)
            );
          } else {
            timeKeepingEntries = newItem.entries;
          }
        } else {
          timeKeepingEntries = newItem.entries;
        }
      } catch (error) {
        timeKeepingEntries = newItem.entries;
      }
      const { data } = await apiUpdateTimeKeeping(item.id, {
        type: (newItem.value as TimeKeepingDay).type,
        notes: (newItem.value as TimeKeepingDay).notes,
        timeKeepingEntries,
      });
      if (data) {
        const sortedEntries = data.timeKeepingEntries.sort((a, b) =>
          moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
        );
        dispatch(updateTimeKeepingDay(data, item.id));
        monthlyCloseChange(data.monthlyCloseId);

        return {
          id: data.timeKeepingDayId,
          groupId: item.groupId,
          title: '',
          type: 'timeKeeping',
          startDateTime: toISOString(sortedEntries[0].startTime),
          endDateTime: toISOString(
            sortedEntries[sortedEntries.length - 1].endTime
          ),
          entries: data.timeKeepingEntries,
          value: data,
          state: 'ok',
        } as TimeAndLeaveManagementTimelineItem;
      } else {
        if (ref.current) {
          ref.current?.forceUpdate();
        }
      }
      return null;
    },
    [timeKeepingPrecision, monthlyCloseChange, dispatch]
  );

  const handleTimeRecordUpdate: (
    item: TimeAndLeaveManagementTimelineItem,
    newItem: TimeAndLeaveManagementTimelineItem
  ) => Promise<TimeAndLeaveManagementTimelineItem> = useCallback(
    async (item, newItem) => {
      const itemValue: TimeRecord = newItem.value as TimeRecord;
      const request: UpdateTimeRecordRequest = {
        timeRecordId: itemValue.timeRecordId,
        projectId: itemValue.projectId,
        projectPhaseId: itemValue.projectPhaseId,
        isSeparateService: itemValue.isSeparateService,
        subProjectId: itemValue.subProjectId,
        description: itemValue.description,
        day: itemValue.day,
        contactId: itemValue.contactId,
        kilometerDistance: itemValue.kilometerDistance ?? 0,
        title: itemValue.title,
        timeRecordEntries: newItem.entries,
        rowVersion: itemValue.rowVersion,
      };
      if (ref.current) {
        ref.current.replaceItem(
          newItem.groupId,
          { ...newItem, disabled: true },
          newItem.id
        );
      }
      const { data } = await apiUpdateTimeRecord(
        request,
        itemValue.timeRecordId,
        itemValue.rowVersion,
        'me'
      );
      if (data) {
        const sortedEntries = data.timeRecordEntries.sort((a, b) =>
          moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
        );
        dispatch(
          updateTimeRecord(data, data.timeRecordId, item.value as TimeRecord)
        );
        return {
          id: data.timeRecordId,
          groupId: data.projectId,
          title: data.title,
          type: 'timeRecord',
          startDateTime: toISOString(sortedEntries[0].startTime),
          endDateTime: toISOString(
            sortedEntries[sortedEntries.length - 1].endTime
          ),
          entries: data.timeRecordEntries,
          value: data,
          state: 'ok',
        } as TimeAndLeaveManagementTimelineItem;
      }
      notification.open({
        message: t('common:error'),
        description: t('timeRecords:errorMessages.updateError'),
      });
      return null;
    },
    [dispatch, t]
  );

  const handleOnItemCreate: (
    groupId: string,
    item: TimeAndLeaveManagementTimelineItem
  ) => Promise<TimeAndLeaveManagementTimelineItem> = useCallback(
    async (groupId, item) => {
      if (groupId === 'addProject') {
        return null;
      }
      if (ref.current) {
        ref.current.replaceItem(
          item.groupId,
          { ...item, disabled: true },
          item.id
        );
      }
      if (groupId === 'timeKeeping') {
        let timeKeepingEntries: CreateTimeKeepingEntry[] = [];

        try {
          const { data: suggestion } = await apiFetchTimeKeepingDaySuggestion(
            toISOString(item.startDateTime),
            toISOString(item.endDateTime)
          );

          if (suggestion) {
            timeKeepingEntries = suggestion.timeKeepingEntries.map(
              ({ startTime, endTime }) =>
                snapToXMinutes(timeKeepingPrecision, startTime, endTime)
            );
          } else {
            timeKeepingEntries = [
              {
                startTime: toISOString(item.startDateTime),
                endTime: toISOString(item.endDateTime),
              },
            ];
          }
        } catch (error) {
          timeKeepingEntries = [
            {
              startTime: toISOString(item.startDateTime),
              endTime: toISOString(item.endDateTime),
            },
          ];
        }
        let _setting = setting;
        if (!_setting) {
          const fetchedSetting = await fetchSetting(userMe);
          _setting = fetchedSetting;
        }

        const { data } = await apiCreateTimeKeepingDay({
          type: getDefaultCreateType(_setting),
          timeKeepingEntries,
        });

        if (data) {
          const sortedEntries = data.timeKeepingEntries.sort((a, b) =>
            moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
          );
          dispatch(createTimeKeepingDay(item.startDateTime, data));
          monthlyCloseChange(data.monthlyCloseId);
          const newTimeKeepingItem: TimeAndLeaveManagementTimelineItem = {
            id: data.timeKeepingDayId,
            groupId: item.groupId,
            startDateTime: toISOString(sortedEntries[0].startTime),
            endDateTime: toISOString(
              sortedEntries[sortedEntries.length - 1].endTime
            ),
            disabled: false,
            type: 'timeKeeping',
            entries: data.timeKeepingEntries,
            title: '',
            value: data,
            state: 'ok',
          };
          setSelectedItem(newTimeKeepingItem);
          return newTimeKeepingItem;
        }

        return null;
      }

      const newTimeRecordItem: TimeAndLeaveManagementTimelineItem = {
        ...item,
        id: createTemporaryId(),
        disabled: false,
        type: 'timeRecord',
        entries: [
          {
            startTime: toISOString(item.startDateTime),
            endTime: toISOString(item.endDateTime),
          },
        ],
        value: null,
        state: 'temporary',
      };
      setSelectedItem(newTimeRecordItem);
      setTemporaryItems([...temporaryItems, newTimeRecordItem]);
      return newTimeRecordItem;
    },
    [
      temporaryItems,
      timeKeepingPrecision,
      setting,
      userMe,
      monthlyCloseChange,
      dispatch,
    ]
  );

  const handleOnBreakPopoverClick: (
    item: TimeAndLeaveManagementTimelineItem,
    group: TimelineGroup,
    popoverPositionInMS: number
  ) => void = useCallback(
    async (item, group, popoverPositionInMS) => {
      const { entries, startDateTime } = item;
      const popoverTime = moment(startDateTime).add(
        Math.round(popoverPositionInMS / (1000 * 60)),
        'minutes'
      );
      const entryToSplit = entries.find(
        ({ startTime, endTime }) =>
          moment(startTime).isBefore(popoverTime) &&
          moment(endTime).isAfter(popoverTime)
      );

      let breakStartTime = popoverTime.toISOString(true).split('.')[0];
      let breakEndTime = popoverTime
        .clone()
        .add(timeKeepingPrecision < 15 ? 20 : 15, 'minutes')
        .toISOString(true)
        .split('.')[0];

      if (group.id === 'timeKeeping' && entryToSplit) {
        const { data: suggestions } = await apiFetchTimeKeepingDaySuggestion(
          entryToSplit.startTime,
          entryToSplit.endTime
        );
        if (suggestions?.timeKeepingEntries?.length === 2) {
          const _suggestions = suggestions.timeKeepingEntries
            .map(({ startTime, endTime }) =>
              snapToXMinutes(timeKeepingPrecision, startTime, endTime)
            )
            .sort((a, b) =>
              moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
            );
          if (_suggestions[0]?.endTime) {
            breakStartTime = toISOString(_suggestions[0]?.endTime);
          }
          if (_suggestions[1]?.startTime) {
            breakEndTime = toISOString(_suggestions[1]?.startTime);
          }
        }
      }

      if (
        entryToSplit &&
        (item.entries.length === 1 ||
          !(
            moment(breakEndTime).isSameOrAfter(moment(entryToSplit.endTime)) ||
            moment(breakStartTime).isSameOrBefore(
              moment(entryToSplit.startTime)
            )
          ))
      ) {
        const newItem: TimeAndLeaveManagementTimelineItem = {
          ...item,
          entries: [
            {
              startTime: toISOString(entryToSplit.startTime),
              endTime: toISOString(breakStartTime),
            },
            {
              startTime: toISOString(breakEndTime),
              endTime: toISOString(entryToSplit.endTime),
            },
            ...item.entries.filter(
              (entry) => entry.startTime !== entryToSplit.startTime
            ),
          ].sort((a, b) =>
            moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
          ),
        };
        if (isTemporaryId(item.id)) {
          const itemIndex = temporaryItems.findIndex(
            (tempItem) => tempItem.id === item.id
          );
          if (itemIndex > -1) {
            const newItems = [...temporaryItems];
            newItems.splice(itemIndex, 1, newItem);
            setTemporaryItems(newItems);
            syncSelectedItem(newItem);
          }
        } else {
          if (group.id === 'timeKeeping') {
            await handleTimeKeepingUpdate(item, newItem);
          } else {
            await handleTimeRecordUpdate(item, newItem);
          }
        }
      }
    },
    [
      temporaryItems,
      timeKeepingPrecision,
      handleTimeKeepingUpdate,
      handleTimeRecordUpdate,
      syncSelectedItem,
    ]
  );

  const handleOnPopoverClick: (
    item: TimeAndLeaveManagementTimelineItem,
    group: TimelineGroup,
    popoverPositionInMS: number
  ) => void = useCallback(
    (item, group, popoverPositionInMS) => {
      if (item.type !== 'timeTrackingSuggestion') {
        handleOnBreakPopoverClick(item, group, popoverPositionInMS);
      }
    },
    [handleOnBreakPopoverClick]
  );

  const handleOnFormCancel = (
    item: TimeAndLeaveManagementTimelineItem,
    type: 'timeKeeping' | 'timeRecord'
  ) => {
    setSelectedItem(null);
    if (isTemporaryId(item.id)) {
      setTemporaryItems(temporaryItems.filter(({ id }) => item.id !== id));
    }
  };
  var timeKeepingList = useSelector(timeKeepingListSelector(currentMonth));
  var timeKeepingAtDay = (day: string): boolean => {
    if (timeKeepingList) {
      for (const timeKeeping of timeKeepingList) {
        if (
          timeKeeping.timeKeepingEntries[0].startTime.substring(0, 10) === day
        ) {
          return true;
        }
      }
    }
    return false;
  };
  const handleOnFormFinish = async (
    previousId: string,
    item: TimeAndLeaveManagementTimelineItem,
    type: 'timeKeeping' | 'timeRecord'
  ) => {
    if (isTemporaryId(previousId)) {
      setTemporaryItems(temporaryItems.filter(({ id }) => previousId !== id));
      setSelectedItem(item);
      if (addedProjectIds.includes(item.groupId)) {
        setAddedProjectIds(addedProjectIds.filter((id) => id !== item.groupId));
      }
    }
    if (timeKeepingAtDay(item.startDateTime.substring(0, 10)) === false) {
      var newItems = _items;
      newItems.push(item);
      const timeRecordsForDay = newItems.filter(
        (item) =>
          item.type === 'timeRecord' &&
          new Date(item.startDateTime).toDateString() ===
            new Date(day).toDateString()
      );

      if (timeRecordsForDay.length !== 0) {
        // Find the earliest start time
        let earliestStartTime = timeRecordsForDay[0].startDateTime;
        timeRecordsForDay.forEach((item) => {
          if (new Date(item.startDateTime) <= new Date(earliestStartTime)) {
            earliestStartTime = item.startDateTime;
          }
        });

        // Find the latest end time
        let latestEndTime = timeRecordsForDay[0].endDateTime;
        timeRecordsForDay.forEach((item) => {
          if (new Date(item.endDateTime) >= new Date(latestEndTime)) {
            latestEndTime = item.endDateTime;
          }
        });

        const startTime = earliestStartTime;
        const endTime = latestEndTime;

        const { data } = await apiFetchTimeKeepingDaySuggestion(
          startTime,
          endTime
        );
        if (data) {
          const newItem = {
            ...item,
            startDateTime: startTime,
            endDateTime: endTime,
            state: 'ok',
            type: 'timeKeepingSuggestion',
            groupId: 'timeKeeping',
            entries: data.timeKeepingEntries,
            value: data,
            title:
              startTime.substring(11, 16) + ' - ' + endTime.substring(11, 16),
          };

          setTimeKeepingSuggestion(
            newItem as TimeAndLeaveManagementTimelineItem
          );
        }
      }
    }
    ref.current?.forceUpdate();
  };

  const handleOnFormChange = (
    item: TimeAndLeaveManagementTimelineItem,
    type: 'timeKeeping' | 'timeRecord'
  ) => {
    setSelectedItem(item);
    if (ref.current) ref.current?.forceUpdate();
  };

  const handleOnFormDelete = (
    id: string,
    groupId: string,
    type: 'timeKeeping' | 'timeRecord'
  ) => {
    setSelectedItem(null);
  };

  const handleOnItemChange: (
    groupId: string,
    item: TimeAndLeaveManagementTimelineItem
  ) => Promise<TimelineItem> = useCallback(
    async (groupId, item) => {
      const sortedEntries = item.entries.sort((a, b) =>
        moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
      );
      const entriesStartTime = moment(
        Math.min(
          ...sortedEntries.map(({ startTime }) => moment(startTime).valueOf())
        )
      )
        .toISOString(true)
        .split('.')[0];
      const entriesEndTime = moment(
        Math.max(
          ...sortedEntries.map(({ endTime }) => moment(endTime).valueOf())
        )
      )
        .toISOString(true)
        .split('.')[0];

      let newItem: TimeAndLeaveManagementTimelineItem = null;
      if (
        !moment(item.startDateTime).isSame(entriesStartTime) &&
        !moment(item.endDateTime).isSame(entriesEndTime)
      ) {
        newItem = calcItemOnMove(item, entriesStartTime, sortedEntries);
      } else {
        newItem = calcOnItemResize(
          item,
          entriesStartTime,
          entriesEndTime,
          sortedEntries
        );
      }
      if (isTemporaryId(item.id)) {
        const itemIndex = temporaryItems.findIndex(
          (tempItem) => tempItem.id === item.id
        );
        if (itemIndex > -1) {
          const newItems = [...temporaryItems];
          newItems.splice(itemIndex, 1, newItem);
          setTemporaryItems(newItems);
        }
      } else {
        if (groupId === 'timeKeeping') {
          newItem = await handleTimeKeepingUpdate(item, newItem);
        } else {
          newItem = await handleTimeRecordUpdate(item, newItem);
        }
      }
      setSelectedItem(newItem);
      return newItem;
    },
    [temporaryItems, handleTimeKeepingUpdate, handleTimeRecordUpdate]
  );

  const handleOnEntriesChange = useCallback(
    async (
      entries: TimeEntry[],
      item: TimeAndLeaveManagementTimelineItem,
      group: TimelineGroup
    ) => {
      if (ref.current) {
        ref.current.setIsFetching(true);
      }
      let newItem: TimeAndLeaveManagementTimelineItem = {
        ...item,
        entries: entries.map(({ startTime, endTime }) => ({
          startTime,
          endTime,
        })),
      };
      if (isTemporaryId(item.id)) {
        setTemporaryItems([
          ...temporaryItems.filter(({ id }) => id !== item.id),
          newItem,
        ]);
        syncSelectedItem(newItem);
        if (ref.current) {
          ref.current.replaceItem(group.id, newItem, item.id);
          ref.current.setIsFetching(false);
        }
        return true;
      } else {
        let result: TimeAndLeaveManagementTimelineItem = null;
        if (group.id === 'timeKeeping') {
          result = await handleTimeKeepingUpdate(item, newItem);
        } else {
          result = await handleTimeRecordUpdate(item, newItem);
        }
        if (ref.current) {
          ref.current.setIsFetching(false);
          if (result) {
            ref.current.replaceItem(group.id, result, item.id);
          } else {
            ref.current.replaceItem(group.id, item, item.id);
          }
        }
        if (result) {
          setSelectedItem(result);
          return true;
        } else {
          return false;
        }
      }
    },
    [
      temporaryItems,
      handleTimeKeepingUpdate,
      handleTimeRecordUpdate,
      syncSelectedItem,
    ]
  );

  const handleOnBreakChange = useCallback(
    async (groupId: string, item: TimeAndLeaveManagementTimelineItem) => {
      if (isTemporaryId(item.id)) {
        setTemporaryItems([
          ...temporaryItems.filter(({ id }) => id !== item.id),
          item,
        ]);
        syncSelectedItem(item);
      } else {
        if (ref.current) {
          ref.current.replaceItem(
            groupId,
            { ...item, disabled: true },
            item.id
          );
          ref.current.setIsFetching(true);
        }
        let result: TimeAndLeaveManagementTimelineItem = null;
        if (groupId === 'timeKeeping') {
          result = await handleTimeKeepingUpdate(item, item);
        } else {
          result = await handleTimeRecordUpdate(item, item);
        }
        if (result) {
          setSelectedItem(result);
        }
        if (ref.current) {
          ref.current.setIsFetching(false);
          ref.current?.forceUpdate();
        }
      }
    },
    [
      temporaryItems,
      handleTimeKeepingUpdate,
      handleTimeRecordUpdate,
      syncSelectedItem,
    ]
  );

  const handleOnItemRemove = useCallback(
    async (item: TimeAndLeaveManagementTimelineItem, group: TimelineGroup) => {
      if (isTemporaryId(item.id)) {
        setTemporaryItems(temporaryItems.filter(({ id }) => id !== item.id));
        if (selectedItem?.id === item.id) {
          setSelectedItem(null);
        }
      } else {
        let _result: Response = null;
        if (group.id === 'timeKeeping') {
          const { result } = await apiDeleteTimeKeepingDayMe(item.id);
          _result = result;
          if (_result?.status >= 200 && _result?.status < 300) {
            const { monthlyCloseId, timeKeepingDayId } =
              item.value as TimeKeepingDay;
            dispatch(deleteTimeKeepingDay(monthlyCloseId, timeKeepingDayId));
            monthlyCloseChange(monthlyCloseId);
          }
        } else {
          const { result } = await apiDeleteTimeRecords(
            item.id,
            item.value as TimeRecord,
            'me'
          );
          _result = result;
          if (_result?.status >= 200 && _result?.status < 300) {
            const { timeRecordId } = item.value as TimeRecord;
            dispatch(
              deleteMyTimeRecord(timeRecordId, item.value as TimeRecord, true)
            );
          }
        }
        if (_result?.status >= 200 && _result?.status < 300) {
          setSelectedItem(null);
        } else if (ref.current) {
          ref.current.replaceItem(group.id, item, item.id);
        }
      }
    },
    [selectedItem, temporaryItems, dispatch, monthlyCloseChange]
  );

  const handleOnTimeRangeChange = useCallback(
    async (
      _visibleStartTime: Date,
      _visibleEndTime: Date,
      _startTime: Date,
      _endTime: Date
    ) => {
      const visibleStartTime = moment(_visibleStartTime);
      const visibleEndTime = moment(_visibleEndTime);
      const startTime = moment(_startTime);
      const endTime = moment(_endTime);
      if (!moment(currentMonth).isSame(visibleStartTime, 'month')) {
        const _startTime = startTime
          .clone()
          .startOf('month')
          .toISOString(true)
          .split('T')[0];
        const _endTime = endTime
          .clone()
          .endOf('month')
          .toISOString(true)
          .split('T')[0];
        dispatch(debounceMonthlyCloseMe(_startTime, _endTime));
        dispatch(debounceTimeRecordsMe(_startTime, _endTime));
        dispatch(debounceAbsencesMe(_startTime, _endTime));
      }
      if (!moment(day).isSame(visibleStartTime, 'week')) {
        const _startTime = startTime
          .clone()
          .startOf('week')
          .toISOString(true)
          .split('T')[0];
        const _endTime = endTime
          .clone()
          .endOf('week')
          .toISOString(true)
          .split('T')[0];
        debouncedApiFetchTimeTrackingSuggestions(
          _startTime,
          _endTime,
          setTimeTrackingSuggestionItems,
          setTimeTrackingSuggestionLoading
        );
      }
      setDay(visibleStartTime.toISOString(true).split('T')[0]);
      setMaxRange(visibleEndTime.toISOString(true).substring(11, 16));
      setMinRange(visibleStartTime.toISOString(true).substring(11, 16));
      setSelectedItem(null);
    },
    [day, currentMonth, dispatch]
  );

  function handleOnClickExecuted(): void {
    setTimeKeepingSuggestion(null);
  }

  const getItemMinWidth = useCallback(
    (
      groupId: string,
      item: TimeAndLeaveManagementTimelineItem,
      pixelsPerMs: number,
      event: InteractJSEvent
    ) => {
      if (!item || !item.entries || item.entries.length === 0) {
        return '0px';
      }

      const { entries } = item;
      const sortedEntries = entries.sort((a, b) =>
        moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
      );
      const gridSnapInMSInPX = 15 * 60 * 1000 * pixelsPerMs;

      if (event.edges.left) {
        const minStartTimeMS = new Date(sortedEntries[0].endTime).getTime();
        const endTimeMS = new Date(
          sortedEntries[sortedEntries.length - 1].endTime
        ).getTime();
        const diff = endTimeMS - minStartTimeMS;
        return `${diff * pixelsPerMs + gridSnapInMSInPX}px`;
      } else if (event.edges.right) {
        const startTimeMS = new Date(sortedEntries[0].startTime).getTime();
        const maxEndTimeMS = new Date(
          sortedEntries[sortedEntries.length - 1].startTime
        ).getTime();
        const diff = maxEndTimeMS - startTimeMS;
        return `${diff * pixelsPerMs + gridSnapInMSInPX}px`;
      }
      return `${
        moment(sortedEntries[sortedEntries.length - 1].endTime).diff(
          moment(sortedEntries[0].startTime),
          'ms'
        ) * pixelsPerMs
      }px`;
    },
    []
  );
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    const fetchTimeTrackingSuggestions = async () => {
      const from = moment(initialDay ?? undefined)
        .startOf('week')
        .toISOString(true)
        .split('T')[0];
      const to = moment(initialDay ?? undefined)
        .endOf('week')
        .toISOString(true)
        .split('T')[0];
      const { data } = await apiFetchTimeTrackingSuggestions(from, to);
      if (data) {
        setTimeTrackingSuggestionItems(
          data.map((item) => {
            const startTime = moment
              .utc(item.start)
              .local()
              .toISOString(true)
              .split('.')[0];
            const endTime = moment
              .utc(item.end)
              .local()
              .toISOString(true)
              .split('.')[0];
            return {
              id: uuid(),
              groupId: item.projectId,
              title: item.name,
              startDateTime: startTime,
              endDateTime: endTime,
              type: 'timeTrackingSuggestion',
              value: item,
              entries: [
                {
                  startTime: startTime,
                  endTime: endTime,
                },
              ],
              state: 'ok',
              disabled: true,
            };
          })
        );
      }
    };
    if (isVisible) {
      fetchTimeTrackingSuggestions();
    }
  }, [initialDay, isVisible]);
  //#endregion

  return (
    <Flex.Column
      className={classNames(classes.root, className, {
        [classes.timeline5]: timeKeepingPrecision === 5,
        [classes.timeline10]: timeKeepingPrecision === 10,
      })}
      childrenGap={theme.old.spacing.defaultPadding}
    >
      <Flex.Item flex={1} position="relative">
        <Timeline
          key={timelineKey}
          id="TimeAndLeaveManagementTimeline"
          ref={ref}
          locale={dateFnsLocale}
          gridSnapInMilliseconds={timeKeepingPrecision * 60 * 1000}
          initialVisibleTimeStart={new Date(`${day}T${minRange}:00`)}
          initialVisibleTimeEnd={buildVisibleEndDate(day, maxRange)}
          initialStartTime={new Date(`${day}T00:00:00`)}
          initialEndTime={addDate(new Date(`${day}T00:00:00`), { days: 1 })}
          items={items}
          groups={groups}
          prefixWidth={128}
          suffixWidth={102}
          disableTopBar
          enableStepper
          disableAdd={isFetchingData || timeTrackingSuggestionLoading}
          disableDrag={isFetchingData || timeTrackingSuggestionLoading}
          disableResize={isFetchingData || timeTrackingSuggestionLoading}
          disableRemove={isFetchingData || timeTrackingSuggestionLoading}
          onTimeRangeChange={handleOnTimeRangeChange}
          onItemCreate={handleOnItemCreate}
          onItemChange={handleOnItemChange}
          onItemClick={handleOnItemClick}
          onItemRemove={handleOnItemRemove}
          onPopoverClick={handleOnPopoverClick}
          itemStyle={calcItemStyle}
          onGetItemMinWidth={getItemMinWidth}
          headerPills={[
            {
              iconProp: ['fal', 'bars-progress'],
              tooltip: t('common:timeline.projecttimesTooltip'),
              children: `${
                timeRecordSum === 0 ? 0 : timeRecordSum / 60
              } h`.replace('.', ','),
            },
          ]}
          itemRenderer={(
            group,
            item: TimeAndLeaveManagementTimelineItem,
            pixelsPerMs,
            gridSnapInMilliseconds
          ) =>
            item.type === 'timeKeepingSuggestion' ||
            item.type === 'timeTrackingSuggestion' ? (
              <TimeTrackingSuggestionTimelineItem group={group} item={item} />
            ) : (
              <TimeKeepingTimelinieItem
                item={item as TimeAndLeaveManagementTimelineItem}
                group={group}
                pixelsPerMs={pixelsPerMs}
                gridSnapInMilliseconds={gridSnapInMilliseconds}
                onEntriesChange={handleOnEntriesChange}
              />
            )
          }
          headerSuffixRenderer={() => (
            <Flex.Row
              width={'100%'}
              height={'100%'}
              alignItems="center"
              justifyContent="center"
            >
              <Flex.Row
                flex={1}
                alignItems="center"
                justifyContent="center"
                height={'100%'}
              >
                <FontAwesomeIcon icon={['fal', 'coffee']} />
              </Flex.Row>
              <Flex.Row
                flex={1}
                borderLeft={theme.old.borders.content}
                alignItems="center"
                justifyContent="center"
                height={'100%'}
              >
                <FontAwesomeIcon icon={['fal', 'sigma']} />
              </Flex.Row>
            </Flex.Row>
          )}
          popoverRenderer={(item: TimeAndLeaveManagementTimelineItem, group) =>
            item.type !== 'absence' && (
              <TimeKeepingTimlinePopover
                item={item}
                group={group}
                onClickExecuted={handleOnClickExecuted}
              />
            )
          }
          groupSuffixRenderer={(
            group,
            items,
            visibleTimeStart,
            visibleTimeEnd,
            startTime,
            endTime
          ) => (
            <GroupSuffix
              group={group}
              items={items as TimeAndLeaveManagementTimelineItem[]}
              visibleTimeStart={moment(visibleTimeStart)}
              visibleTimeEnd={moment(visibleTimeEnd)}
              startTime={moment(startTime)}
              endTime={moment(endTime)}
              timelineRef={ref}
              onItemChange={handleOnItemChange}
              onItemCreate={handleOnItemCreate}
              onBreakChange={handleOnBreakChange}
            />
          )}
          groupHeaderRenderer={(group, items) => (
            <GroupHeader
              className={classes.groupHeaderTitle}
              group={group}
              items={items}
              type={
                group.id === 'addProject'
                  ? 'addProject'
                  : group.id === 'timeKeeping'
                    ? 'timeKeeping'
                    : 'timeRecord'
              }
              projectIds={projectGroups.map(({ id }) => id)}
              onProjectSelect={handleOnProjectSelect}
              onProjectRemove={handleOnProjectRemove}
            />
          )}
        />
        {(isFetchingData || timeTrackingSuggestionLoading) && (
          <div className={classes.spinnerContainer}>
            <PrioSpinner size="large" />
          </div>
        )}
      </Flex.Item>
      {selectedItem && (
        <TimeAndLeaveTimelineFormManager
          className={classes.formManager}
          selectedItem={selectedItem}
          timeKeepingGeneralSettings={timeKeepingGeneralSettings}
          onCancel={handleOnFormCancel}
          onFinish={handleOnFormFinish}
          onChange={handleOnFormChange}
          onDelete={handleOnFormDelete}
          onSaveTimeKeepingEntries={handleTimeKeepingUpdate}
        />
      )}
    </Flex.Column>
  );
};

export default TimeKeepingDrawerContent;
